import * as React from 'react';
import { Check, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Shows a dash instead of a tick — e.g. a "select all" box when only some rows are selected. */
  indeterminate?: boolean;
}

/**
 * Theme-styled checkbox. Still a native <input type="checkbox"> underneath (same
 * props, keyboard and label behaviour), with the browser's own box hidden and a
 * themed box + icon drawn in its place, so it matches light and dark mode.
 */
export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, indeterminate = false, ...props }, ref) => {
    const inner = React.useRef<HTMLInputElement>(null);
    React.useImperativeHandle(ref, () => inner.current as HTMLInputElement);

    React.useEffect(() => {
      if (inner.current) inner.current.indeterminate = indeterminate;
    }, [indeterminate]);

    return (
      <span className="relative inline-flex h-4 w-4 shrink-0 align-middle">
        <input
          ref={inner}
          type="checkbox"
          className={cn(
            'peer h-4 w-4 shrink-0 cursor-pointer appearance-none rounded border border-input bg-background transition-colors',
            'checked:border-primary checked:bg-primary indeterminate:border-primary indeterminate:bg-primary',
            'hover:border-primary/70',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
          {...props}
        />
        {indeterminate ? (
          <Minus
            strokeWidth={3}
            className="pointer-events-none absolute inset-0 m-auto h-3 w-3 text-primary-foreground"
          />
        ) : (
          <Check
            strokeWidth={3}
            className="pointer-events-none absolute inset-0 m-auto h-3 w-3 text-primary-foreground opacity-0 transition-opacity peer-checked:opacity-100"
          />
        )}
      </span>
    );
  },
);
Checkbox.displayName = 'Checkbox';
